import React from 'react';
import { MdShield, MdPolicy, MdContacts, MdUpdate } from 'react-icons/md';
import { useTranslation } from '../../hooks/useTranslation';
import { Header, Footer } from '../../../portfolio/layouts/index';
import * as styles from './privacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <Header />
      <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <MdShield className={styles.headerIcon} aria-hidden="true" />
          <div>
            <h1 className={styles.title}>
              {t('privacyPolicy.title')}
            </h1>
            <p className={styles.subtitle}>
              {t('privacyPolicy.subtitle')}
            </p>
          </div>
        </div>
        <div className={styles.lastUpdated}>
          <MdUpdate aria-hidden="true" />
          <span>{t('privacyPolicy.lastUpdated')}: 7 août 2025</span>
        </div>
      </header>

      <main className={styles.content}>
        <nav className={styles.tableOfContents} aria-label="Sommaire">
          <h2 className={styles.tocTitle}>{t('privacyPolicy.toc.title')}</h2>
          <ul className={styles.tocList}>
            <li><a href="#introduction">{t('privacyPolicy.sections.introduction.title')}</a></li>
            <li><a href="#data-collection">{t('privacyPolicy.sections.dataCollection.title')}</a></li>
            <li><a href="#data-usage">{t('privacyPolicy.sections.dataUsage.title')}</a></li>
            <li><a href="#cookies">{t('privacyPolicy.sections.cookies.title')}</a></li>
            <li><a href="#data-sharing">{t('privacyPolicy.sections.dataSharing.title')}</a></li>
            <li><a href="#data-security">{t('privacyPolicy.sections.dataSecurity.title')}</a></li>
            <li><a href="#user-rights">{t('privacyPolicy.sections.userRights.title')}</a></li>
            <li><a href="#contact">{t('privacyPolicy.sections.contact.title')}</a></li>
          </ul>
        </nav>

        <article className={styles.article}>
          <section id="introduction" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <MdPolicy aria-hidden="true" />
              {t('privacyPolicy.sections.introduction.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.introduction.content1')}</p>
              <p>{t('privacyPolicy.sections.introduction.content2')}</p>
              <div className={styles.highlight}>
                <strong>{t('privacyPolicy.sections.introduction.highlight')}</strong>
              </div>
            </div>
          </section>

          <section id="data-collection" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.dataCollection.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.dataCollection.intro')}</p>
              
              <h3 className={styles.subsectionTitle}>
                {t('privacyPolicy.sections.dataCollection.automatic.title')}
              </h3>
              <ul className={styles.dataList}>
                <li>{t('privacyPolicy.sections.dataCollection.automatic.ip')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.automatic.browser')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.automatic.device')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.automatic.pages')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.automatic.duration')}</li>
              </ul>

              <h3 className={styles.subsectionTitle}>
                {t('privacyPolicy.sections.dataCollection.voluntary.title')}
              </h3>
              <ul className={styles.dataList}>
                <li>{t('privacyPolicy.sections.dataCollection.voluntary.name')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.voluntary.email')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.voluntary.message')}</li>
                <li>{t('privacyPolicy.sections.dataCollection.voluntary.preferences')}</li>
              </ul>
            </div>
          </section>

          <section id="data-usage" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.dataUsage.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.dataUsage.intro')}</p>
              <ul className={styles.purposeList}>
                <li>
                  <strong>{t('privacyPolicy.sections.dataUsage.functionality.title')}:</strong>
                  {t('privacyPolicy.sections.dataUsage.functionality.desc')}
                </li>
                <li>
                  <strong>{t('privacyPolicy.sections.dataUsage.communication.title')}:</strong>
                  {t('privacyPolicy.sections.dataUsage.communication.desc')}
                </li>
                <li>
                  <strong>{t('privacyPolicy.sections.dataUsage.analytics.title')}:</strong>
                  {t('privacyPolicy.sections.dataUsage.analytics.desc')}
                </li>
                <li>
                  <strong>{t('privacyPolicy.sections.dataUsage.security.title')}:</strong>
                  {t('privacyPolicy.sections.dataUsage.security.desc')}
                </li>
              </ul>
            </div>
          </section>

          <section id="cookies" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.cookies.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.cookies.intro')}</p>
              
              <div className={styles.cookieTypes}>
                <div className={styles.cookieType}>
                  <h3 className={styles.cookieTypeTitle}>
                    {t('privacyPolicy.sections.cookies.necessary.title')}
                    <span className={styles.required}>{t('privacyPolicy.required')}</span>
                  </h3>
                  <p>{t('privacyPolicy.sections.cookies.necessary.desc')}</p>
                  <ul className={styles.cookieList}>
                    <li><code>cookie-consent</code>: {t('privacyPolicy.sections.cookies.necessary.consent')}</li>
                    <li><code>theme-preference</code>: {t('privacyPolicy.sections.cookies.necessary.theme')}</li>
                    <li><code>language-preference</code>: {t('privacyPolicy.sections.cookies.necessary.language')}</li>
                  </ul>
                </div>

                <div className={styles.cookieType}>
                  <h3 className={styles.cookieTypeTitle}>
                    {t('privacyPolicy.sections.cookies.analytics.title')}
                    <span className={styles.optional}>{t('privacyPolicy.optional')}</span>
                  </h3>
                  <p>{t('privacyPolicy.sections.cookies.analytics.desc')}</p>
                  <ul className={styles.cookieList}>
                    <li><code>_ga</code>: Google Analytics (2 ans)</li>
                    <li><code>_ga_*</code>: Google Analytics 4 (2 ans)</li>
                  </ul>
                </div>

                <div className={styles.cookieType}>
                  <h3 className={styles.cookieTypeTitle}>
                    {t('privacyPolicy.sections.cookies.marketing.title')}
                    <span className={styles.optional}>{t('privacyPolicy.optional')}</span>
                  </h3>
                  <p>{t('privacyPolicy.sections.cookies.marketing.desc')}</p>
                </div>
              </div>
            </div>
          </section>

          <section id="data-sharing" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.dataSharing.title')}
            </h2>
            <div className={styles.sectionContent}>
              <div className={styles.noSharingBanner}>
                <strong>{t('privacyPolicy.sections.dataSharing.noSharing')}</strong>
              </div>
              <p>{t('privacyPolicy.sections.dataSharing.content1')}</p>
              
              <h3 className={styles.subsectionTitle}>
                {t('privacyPolicy.sections.dataSharing.thirdParty.title')}
              </h3>
              <ul className={styles.servicesList}>
                <li>
                  <strong>Google Analytics</strong>: {t('privacyPolicy.sections.dataSharing.thirdParty.analytics')}
                  <br />
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    Politique de confidentialité Google
                  </a>
                </li>
                <li>
                  <strong>Hostinger VPS</strong>: {t('privacyPolicy.sections.dataSharing.thirdParty.hosting')}
                  <br />
                  <a href="https://www.hostinger.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Politique de confidentialité Hostinger
                  </a>
                </li>
                <li>
                  <strong>EmailJS</strong>: {t('privacyPolicy.sections.dataSharing.thirdParty.emailjs')}
                  <br />
                  <a href="https://www.emailjs.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Politique de confidentialité EmailJS
                  </a>
                </li>
                <li>
                  <strong>Google reCAPTCHA</strong>: {t('privacyPolicy.sections.dataSharing.thirdParty.recaptcha')}
                  <br />
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    Politique de confidentialité Google
                  </a>
                </li>
              </ul>
            </div>
          </section>

          <section id="data-security" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.dataSecurity.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.dataSecurity.intro')}</p>
              <ul className={styles.securityList}>
                <li>{t('privacyPolicy.sections.dataSecurity.https')}</li>
                <li>{t('privacyPolicy.sections.dataSecurity.headers')}</li>
                <li>{t('privacyPolicy.sections.dataSecurity.rateLimit')}</li>
                <li>{t('privacyPolicy.sections.dataSecurity.validation')}</li>
                <li>{t('privacyPolicy.sections.dataSecurity.monitoring')}</li>
              </ul>
            </div>
          </section>

          <section id="user-rights" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('privacyPolicy.sections.userRights.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.userRights.intro')}</p>
              <div className={styles.rightsGrid}>
                <div className={styles.right}>
                  <h3>{t('privacyPolicy.sections.userRights.access.title')}</h3>
                  <p>{t('privacyPolicy.sections.userRights.access.desc')}</p>
                </div>
                <div className={styles.right}>
                  <h3>{t('privacyPolicy.sections.userRights.rectification.title')}</h3>
                  <p>{t('privacyPolicy.sections.userRights.rectification.desc')}</p>
                </div>
                <div className={styles.right}>
                  <h3>{t('privacyPolicy.sections.userRights.erasure.title')}</h3>
                  <p>{t('privacyPolicy.sections.userRights.erasure.desc')}</p>
                </div>
                <div className={styles.right}>
                  <h3>{t('privacyPolicy.sections.userRights.portability.title')}</h3>
                  <p>{t('privacyPolicy.sections.userRights.portability.desc')}</p>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <MdContacts aria-hidden="true" />
              {t('privacyPolicy.sections.contact.title')}
            </h2>
            <div className={styles.sectionContent}>
              <p>{t('privacyPolicy.sections.contact.intro')}</p>
              <div className={styles.contactInfo}>
                <div className={styles.contactMethod}>
                  <strong>{t('privacyPolicy.sections.contact.email')}:</strong>
                  <a href="mailto:jf.c.webcraft@gmail.com">jf.c.webcraft@gmail.com</a>
                </div>
                <div className={styles.contactMethod}>
                  <strong>{t('privacyPolicy.sections.contact.response')}:</strong>
                  {t('privacyPolicy.sections.contact.responseTime')}
                </div>
              </div>
              
              <div className={styles.authorityInfo}>
                <h3>{t('privacyPolicy.sections.contact.authority.title')}</h3>
                <p>{t('privacyPolicy.sections.contact.authority.desc')}</p>
                <a 
                  href="https://www.cnil.fr/fr/plaintes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.authorityLink}
                >
                  {t('privacyPolicy.sections.contact.authority.link')}
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>
    </div>
    <Footer />
    </>
  );
};

export default PrivacyPolicy;
